import { useEffect } from 'react';
import { CONSENT_EVENT, getConsent, type ConsentValue } from '~/lib/consent';
import { parseConsentPolicy, strictConsentPolicy, type ConsentRegime } from '~/lib/consentRegime';
import { initFirebaseAnalytics } from '~/lib/firebase';

// Third-party analytics and marketing scripts ported from the old Webflow site.
// Google tags and cookieless tools load immediately on live hosts; heavier
// visitor-identification trackers load only after consent. Client-side injection
// avoids SSR hydration mismatches.

const PROD_HOSTS = ['website.dev.nsave.com', 'nsave.com', 'www.nsave.com'];

const GTM_CONTAINER_ID = 'GTM-P8RP7K4V';
const GA_MEASUREMENT_ID = 'G-KE17NLRZ32';
const PLAUSIBLE_DOMAIN = 'nsave.com';
const AHREFS_KEY = 'aXmFQAj8nar72Ub99bXyMA';
const FB_APP_ID = '964897222376610';
const META_PIXEL_ID = '1326939099590151';
const CLARITY_SRC =
  'https://cdn.prod.website-files.com/66de9fe4a74bb719d845a843%2F652d31f3dc22d7b4ee708e44%2F6a2340e41c9787a31dcc3f10%2Fclarity_script-2.9.8.js';

type GoogleConsentStatus = 'granted' | 'denied';
type GoogleConsentCommand = 'default' | 'update';

/**
 * Google Consent Mode splits storage by purpose, and our two purposes follow
 * different rules: analytics may be exempt from prior consent depending on the
 * visitor's country, advertising never is.
 */
interface ConsentState {
  analytics: GoogleConsentStatus;
  ads: GoogleConsentStatus;
}

declare global {
  interface Window {
    __nsaveAlwaysLoaded?: boolean;
    __nsaveGatedLoaded?: boolean;
  }
}

function addScript(options: {
  src?: string;
  inline?: string;
  async?: boolean;
  defer?: boolean;
  attrs?: Record<string, string>;
}) {
  const element = document.createElement('script');
  if (options.src) element.src = options.src;
  if (options.inline) element.text = options.inline;
  if (options.async) element.async = true;
  if (options.defer) element.defer = true;
  if (options.attrs) {
    for (const [key, value] of Object.entries(options.attrs)) {
      element.setAttribute(key, value);
    }
  }
  document.head.appendChild(element);
}

function getConsentState(consent: ConsentValue | null, regime: ConsentRegime): ConsentState {
  const accepted = consent === 'accepted';

  return {
    analytics: accepted || regime === 'analytics-always' ? 'granted' : 'denied',
    ads: accepted ? 'granted' : 'denied',
  };
}

function googleConsentInline(command: GoogleConsentCommand, state: ConsentState) {
  return (
    'window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}' +
    `gtag('consent','${command}',{ad_personalization:'${state.ads}',ad_storage:'${state.ads}',` +
    `ad_user_data:'${state.ads}',analytics_storage:'${state.analytics}'});`
  );
}

function updateGoogleConsent(state: ConsentState) {
  addScript({ inline: googleConsentInline('update', state) });
}

function loadAlways(state: ConsentState) {
  if (window.__nsaveAlwaysLoaded) return;
  window.__nsaveAlwaysLoaded = true;

  addScript({
    inline: googleConsentInline('default', state),
  });
  addScript({
    inline: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${GTM_CONTAINER_ID}');`,
  });
  addScript({
    src: `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`,
    async: true,
  });
  addScript({
    inline: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${GA_MEASUREMENT_ID}');`,
  });
  addScript({
    src: 'https://plausible.io/js/script.js',
    defer: true,
    attrs: { 'data-domain': PLAUSIBLE_DOMAIN },
  });
  addScript({
    src: 'https://analytics.ahrefs.com/analytics.js',
    async: true,
    attrs: { 'data-key': AHREFS_KEY },
  });
}

function loadConsented() {
  if (window.__nsaveGatedLoaded) return;
  window.__nsaveGatedLoaded = true;

  addScript({ src: CLARITY_SRC });

  if (!document.getElementById('fb-root')) {
    const facebookRoot = document.createElement('div');
    facebookRoot.id = 'fb-root';
    document.body.insertBefore(facebookRoot, document.body.firstChild);
  }
  addScript({
    inline: `window.fbAsyncInit=function(){FB.init({appId:'${FB_APP_ID}',xfbml:true,version:'v22.0'});FB.AppEvents.logPageView();};(function(d,s,id){var js,fjs=d.getElementsByTagName(s)[0];if(d.getElementById(id)){return;}js=d.createElement(s);js.id=id;js.src="https://connect.facebook.net/en_US/sdk.js";fjs.parentNode.insertBefore(js,fjs);}(document,'script','facebook-jssdk'));`,
  });
  addScript({
    inline: `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${META_PIXEL_ID}');fbq('track','PageView');`,
  });

  // Firebase Analytics (GA4 — Firebase project `nsave1`). Team can use other
  // Firebase products anytime via getFirebaseApp() in ~/lib/firebase.
  void initFirebaseAnalytics();

  // Preserve the website's existing Leadsy integration, but gate it with the
  // other visitor-identification trackers instead of loading it before consent.
  if (window.location.hostname === 'www.nsave.com') {
    const leadsy = document.createElement('script');
    leadsy.id = 'vtag-ai-js';
    leadsy.async = true;
    leadsy.src = 'https://r2.leadsy.ai/tag.js';
    leadsy.dataset.pid = '1rfsxjcMjuHtXnZE4';
    leadsy.dataset.version = '062024';
    document.body.append(leadsy);
  }
}

/**
 * The visitor's country is only known server-side (CloudFront geo header), and
 * the HTML is CDN-cached, so the regime arrives over a separate request. Google
 * tags therefore load before it resolves, on strict defaults, and the answer is
 * applied as a `consent update` — never the other way round, so a slow or
 * failed lookup cannot leak tracking into a strict jurisdiction.
 */
async function fetchConsentRegime(signal: AbortSignal): Promise<ConsentRegime> {
  try {
    const response = await fetch('/api/consent-regime', {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!response.ok) return strictConsentPolicy.regime;
    return parseConsentPolicy((await response.json()) as unknown).regime;
  } catch {
    return strictConsentPolicy.regime;
  }
}

export function Analytics() {
  useEffect(() => {
    if (!PROD_HOSTS.includes(window.location.hostname)) return;

    const controller = new AbortController();
    let consent = getConsent();
    let regime: ConsentRegime = strictConsentPolicy.regime;

    loadAlways(getConsentState(consent, regime));
    if (consent === 'accepted') loadConsented();

    const applyConsent = () => {
      updateGoogleConsent(getConsentState(consent, regime));
      if (consent === 'accepted') loadConsented();
    };

    const onConsent = (event: Event) => {
      consent = (event as CustomEvent<ConsentValue>).detail;
      applyConsent();
    };
    window.addEventListener(CONSENT_EVENT, onConsent);

    void fetchConsentRegime(controller.signal).then((resolved) => {
      if (resolved === regime) return;
      regime = resolved;
      applyConsent();
    });

    return () => {
      controller.abort();
      window.removeEventListener(CONSENT_EVENT, onConsent);
    };
  }, []);

  return null;
}
